import React, { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { checkPhoneExists } from "../firebase/firebase";
import styles from "./RegisterPage.module.css";

const RegisterPage: React.FC = () => {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [gender, setGender] = useState("male");
  const [birthdate, setBirthdate] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptNotifications, setAcceptNotifications] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [emailChecked, setEmailChecked] = useState(false);
  const [phoneChecked, setPhoneChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [phoneError, setPhoneError] = useState("");

  // 카카오 회원가입 관련 상태
  const [isKakaoSignup, setIsKakaoSignup] = useState(false);
  const [kakaoInfo, setKakaoInfo] = useState<any>(null);

  // 네이버 회원가입 관련 상태
  const [isNaverSignup, setIsNaverSignup] = useState(false);
  const [naverInfo, setNaverInfo] = useState<any>(null);

  const {
    signUp,
    checkEmail,
    updateUserProfile,
    signUpWithKakao,
    signUpWithNaver,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Firebase recaptcha 컨테이너 제거 및 소셜 회원가입 초기화
  useEffect(() => {
    const initializeSocialSignup = async () => {
      // 카카오 회원가입 데이터 확인 (location.state 또는 localStorage)
      let kakaoData = null;
      let naverData = null;

      // 1. location.state에서 카카오 정보 확인
      if (location.state?.fromKakao && location.state?.kakaoInfo) {
        kakaoData = location.state.kakaoInfo;
        console.log("location.state에서 카카오 정보 확인:", kakaoData);
      }

      // 2. localStorage에서 카카오 정보 확인
      if (!kakaoData) {
        const storedKakaoInfo = localStorage.getItem("kakaoSignupInfo");
        if (storedKakaoInfo) {
          try {
            kakaoData = JSON.parse(storedKakaoInfo);
            console.log("localStorage에서 카카오 정보 확인:", kakaoData);
          } catch (error) {
            console.error("localStorage 카카오 정보 파싱 오류:", error);
            localStorage.removeItem("kakaoSignupInfo");
          }
        }
      }

      // 3. location.state에서 네이버 정보 확인
      if (location.state?.fromNaver && location.state?.naverInfo) {
        naverData = location.state.naverInfo;
        console.log("location.state에서 네이버 정보 확인:", naverData);
      }

      // 4. localStorage에서 네이버 정보 확인
      if (!naverData) {
        const storedNaverInfo = localStorage.getItem("naverSignupInfo");
        if (storedNaverInfo) {
          try {
            naverData = JSON.parse(storedNaverInfo);
            console.log("localStorage에서 네이버 정보 확인:", naverData);
          } catch (error) {
            console.error("localStorage 네이버 정보 파싱 오류:", error);
            localStorage.removeItem("naverSignupInfo");
          }
        }
      }

      // 카카오 데이터가 있는 경우 카카오 회원가입 모드
      if (kakaoData) {
        try {
          setLoading(true);
          setIsKakaoSignup(true);
          setKakaoInfo(kakaoData);
          setIsNaverSignup(false);
          setNaverInfo(null);

          // 카카오에서 받은 정보로 초기값 설정
          if (kakaoData.email) {
            setEmail(kakaoData.email);
            setEmailChecked(true); // 카카오 이메일은 검증된 것으로 간주
          }
          if (kakaoData.name) {
            setName(kakaoData.name);
          }

          // 카카오 회원가입은 비밀번호가 필요없으므로 2단계로 이동
          setStep(2);

          console.log("카카오 회원가입 초기화 완료");
        } catch (error) {
          console.error("카카오 정보 설정 실패:", error);
          setError("카카오 정보 설정에 실패했습니다. 다시 시도해주세요.");
          // 카카오 정보 정리
          localStorage.removeItem("kakaoSignupInfo");
          navigate("/login");
        } finally {
          setLoading(false);
        }
      }
      // 네이버 데이터가 있는 경우 네이버 회원가입 모드
      else if (naverData) {
        try {
          setLoading(true);
          setIsNaverSignup(true);
          setNaverInfo(naverData);
          setIsKakaoSignup(false);
          setKakaoInfo(null);

          // 네이버에서 받은 정보로 초기값 설정
          if (naverData.email) {
            setEmail(naverData.email);
            setEmailChecked(true); // 네이버 이메일은 검증된 것으로 간주
          }
          if (naverData.name) {
            setName(naverData.name);
          }

          // 네이버 회원가입은 비밀번호가 필요없으므로 2단계로 이동
          setStep(2);

          console.log("네이버 회원가입 초기화 완료");
        } catch (error) {
          console.error("네이버 정보 설정 실패:", error);
          setError("네이버 정보 설정에 실패했습니다. 다시 시도해주세요.");
          // 네이버 정보 정리
          localStorage.removeItem("naverSignupInfo");
          navigate("/login");
        } finally {
          setLoading(false);
        }
      } else {
        // 소셜 데이터가 없고 소셜 회원가입으로 접근한 경우 (잘못된 접근)
        if (location.state?.fromKakao) {
          console.warn("카카오 회원가입으로 접근했으나 카카오 데이터가 없음");
          setError("카카오 로그인 정보가 없습니다. 다시 로그인해주세요.");
          navigate("/login");
          return;
        }
        if (location.state?.fromNaver) {
          console.warn("네이버 회원가입으로 접근했으나 네이버 데이터가 없음");
          setError("네이버 로그인 정보가 없습니다. 다시 로그인해주세요.");
          navigate("/login");
          return;
        }

        // 일반 회원가입 모드
        console.log("일반 회원가입 모드");
        setIsKakaoSignup(false);
        setKakaoInfo(null);
        setIsNaverSignup(false);
        setNaverInfo(null);
      }
    };

    initializeSocialSignup();

    // cleanup function: 컴포넌트 언마운트 시 소셜 정보 정리
    return () => {
      if (isKakaoSignup) {
        localStorage.removeItem("kakaoSignupInfo");
      }
      if (isNaverSignup) {
        localStorage.removeItem("naverSignupInfo");
      }
    };
  }, [location.state, navigate, isKakaoSignup, isNaverSignup]);

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    } else {
      navigate(-1);
    }
  };

  // 이메일 중복 체크
  const handleEmailCheck = async () => {
    if (!email) {
      setError("이메일을 입력해주세요.");
      return;
    }

    if (!validateEmail(email)) {
      setError("유효한 이메일 주소를 입력해주세요.");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const normalizedEmail = email.toLowerCase().trim();
      const exists = await checkEmail(normalizedEmail);

      if (exists === true) {
        setError("이미 사용 중인 이메일입니다.");
        setEmailChecked(false);
      } else if (exists === false) {
        setError("");
        setEmailChecked(true);
      } else {
        setError("이메일 확인 결과가 불명확합니다. 다시 시도해주세요.");
        setEmailChecked(false);
      }
    } catch (err) {
      console.error("이메일 확인 오류:", err);
      setError("이메일 확인 중 오류가 발생했습니다.");
      setEmailChecked(false);
    } finally {
      setLoading(false);
    }
  };

  // 이메일 유효성 검사
  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  // 비밀번호 유효성 검사
  const validatePassword = (password: string) => {
    return password.length >= 6;
  };

  // 전화번호 중복 확인 (인증 없이 중복 검사만)
  const handleCheckPhone = async () => {
    if (!validatePhone(phone)) {
      setPhoneError("유효한 전화번호 형식이 아닙니다 (예: 010-0000-0000)");
      return;
    }

    try {
      setLoading(true);
      console.log("전화번호 중복 확인 시작:", phone);

      const exists = await checkPhoneExists(phone);

      if (exists) {
        setPhoneError("이미 사용 중인 전화번호입니다");
        setPhoneVerified(false);
        setPhoneChecked(false);
      } else {
        setPhoneError("");
        setPhoneVerified(true);
        setPhoneChecked(true);
      }
    } catch (error) {
      console.error("전화번호 중복 확인 중 오류 발생:", error);
      setPhoneError(
        "전화번호 확인 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요."
      );
      setPhoneVerified(false);
      setPhoneChecked(false);
    } finally {
      setLoading(false);
    }
  };

  // 휴대폰 번호 유효성 검사
  const validatePhone = (phone: string) => {
    const cleanedPhone = phone.replace(/-/g, "");
    const re = /^01([0|1|6|7|8|9])([0-9]{3,4})([0-9]{4})$/;
    return re.test(cleanedPhone);
  };

  const goToNextStep = () => {
    setError("");

    if (step === 1) {
      if (isKakaoSignup || isNaverSignup) {
        // 카카오 또는 네이버 회원가입은 이메일과 이름만 확인
        if (!email || !name) {
          setError("이메일과 이름을 입력해주세요.");
          return;
        }
      } else {
        // 일반 회원가입은 모든 필드 확인
        if (!email || !password || !confirmPassword) {
          setError("모든 필드를 입력해주세요.");
          return;
        }
        if (!emailChecked) {
          setError("이메일 중복 확인을 해주세요.");
          return;
        }
        if (!validatePassword(password)) {
          setError("비밀번호는 6자 이상이어야 합니다.");
          return;
        }
        if (password !== confirmPassword) {
          setError("비밀번호가 일치하지 않습니다.");
          return;
        }
      }
    } else if (step === 2) {
      if (!name || !phone) {
        setError("이름과 전화번호를 입력해주세요.");
        return;
      }
      if (!phoneChecked) {
        setError("전화번호 중복 확인을 완료해주세요.");
        return;
      }
    } else if (step === 3) {
      if (!birthdate) {
        setError("생년월일을 입력해주세요.");
        return;
      }
      if (!acceptTerms) {
        setError("서비스 이용약관에 동의해주세요.");
        return;
      }
    }

    if (step < 3) {
      setStep(step + 1);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!acceptTerms) {
      setError("서비스 이용약관에 동의해주세요.");
      return;
    }

    try {
      setLoading(true);
      setError("");

      if (isKakaoSignup && kakaoInfo) {
        // 카카오 회원가입 처리
        const signupData = {
          kakaoId: kakaoInfo.kakaoId,
          email,
          name,
          phone,
          profileImage: kakaoInfo.profileImage,
          petInfo: null, // 펫 정보는 나중에 추가할 수 있음
        };

        await signUpWithKakao(signupData);
      } else if (isNaverSignup && naverInfo) {
        // 네이버 회원가입 처리
        const signupData = {
          naverId: naverInfo.naverId,
          email,
          name,
          phone,
          profileImage: naverInfo.profileImage,
          petInfo: null, // 펫 정보는 나중에 추가할 수 있음
        };

        await signUpWithNaver(signupData);
      } else {
        // 일반 이메일 회원가입 처리
        const userCredential = await signUp(email, password, name);

        await updateUserProfile(
          userCredential.uid,
          name,
          phone,
          gender,
          birthdate,
          acceptNotifications
        );
      }

      // 카카오 또는 네이버 회원가입인 경우 localStorage 정리
      if (isKakaoSignup) {
        localStorage.removeItem("kakaoSignupInfo");
      }
      if (isNaverSignup) {
        localStorage.removeItem("naverSignupInfo");
      }

      console.log(
        isKakaoSignup
          ? "카카오 회원가입 완료"
          : isNaverSignup
          ? "네이버 회원가입 완료"
          : "일반 회원가입 완료"
      );
      navigate("/");
    } catch (err: any) {
      let errorMessage = "회원가입에 실패했습니다.";

      if (isKakaoSignup || isNaverSignup) {
        if (err.message?.includes("이미 사용 중인 이메일")) {
          errorMessage = "이미 사용 중인 이메일입니다.";
        } else if (err.message?.includes("이미 사용 중인 전화번호")) {
          errorMessage = "이미 사용 중인 전화번호입니다.";
        }
      } else {
        if (err.code === "auth/email-already-in-use") {
          errorMessage = "이미 사용 중인 이메일입니다.";
        } else if (err.code === "auth/weak-password") {
          errorMessage = "비밀번호가 너무 약합니다.";
        }
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getStepTitle = () => {
    switch (step) {
      case 1:
        return "계정 정보";
      case 2:
        return "개인 정보";
      case 3:
        return "약관 동의";
      default:
        return "회원가입";
    }
  };

  return (
    <div className={styles.registerPage}>
      {/* 헤더 */}
      <div className={styles.header}>
        <button className={styles.backButton} onClick={handleBack}>
          <img src="/images/icons/arrow-left.svg" alt="뒤로가기" />
        </button>
        <h1>{getStepTitle()}</h1>
        <div></div>
      </div>

      <div className={styles.container}>
        {/* 진행 표시기 */}
        <div className={styles.progressSection}>
          <div className={styles.progressBar}>
            <div
              className={styles.progressFill}
              style={{ width: `${(step / 3) * 100}%` }}
            ></div>
          </div>
          <div className={styles.progressText}>{step} / 3 단계</div>
        </div>

        {/* 폼 섹션 */}
        <div className={styles.formSection}>
          {error && (
            <div className={styles.errorMessage}>
              <img src="/images/icons/error.svg" alt="오류" />
              <span>{error}</span>
            </div>
          )}

          <form
            onSubmit={step === 3 ? handleSubmit : (e) => e.preventDefault()}
          >
            {step === 1 && (
              <div className={styles.stepContent}>
                <div className={styles.stepHeader}>
                  <h2>
                    {isKakaoSignup || isNaverSignup
                      ? "카카오 또는 네이버 계정 정보 확인"
                      : "계정 정보를 입력해주세요"}
                  </h2>
                  <p>
                    {isKakaoSignup || isNaverSignup
                      ? "카카오 또는 네이버에서 받은 정보를 확인하고 수정해주세요"
                      : "펫밀리 서비스 이용을 위한 기본 정보입니다"}
                  </p>
                </div>

                <div className={styles.inputGroup}>
                  <label htmlFor="email">이메일</label>
                  <div className={styles.inputWithButton}>
                    <div className={styles.inputWrapper}>
                      <img src="/images/icons/email.svg" alt="이메일" />
                      <input
                        type="email"
                        id="email"
                        placeholder="이메일을 입력해주세요"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          setEmailChecked(false);
                        }}
                        required
                        disabled={
                          emailChecked || isKakaoSignup || isNaverSignup
                        }
                      />
                    </div>
                    {!isKakaoSignup && !isNaverSignup && (
                      <button
                        type="button"
                        onClick={handleEmailCheck}
                        disabled={loading || emailChecked}
                        className={`${styles.checkButton} ${
                          emailChecked ? styles.checked : ""
                        }`}
                      >
                        {emailChecked ? "확인완료" : "중복확인"}
                      </button>
                    )}
                  </div>
                </div>

                {!isKakaoSignup && !isNaverSignup && (
                  <>
                    <div className={styles.inputGroup}>
                      <label htmlFor="password">비밀번호</label>
                      <div className={styles.inputWrapper}>
                        <img src="/images/icons/password.svg" alt="비밀번호" />
                        <input
                          type="password"
                          id="password"
                          placeholder="6자 이상 입력해주세요"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          minLength={6}
                        />
                      </div>
                    </div>

                    <div className={styles.inputGroup}>
                      <label htmlFor="confirmPassword">비밀번호 확인</label>
                      <div className={styles.inputWrapper}>
                        <img
                          src="/images/icons/password.svg"
                          alt="비밀번호 확인"
                        />
                        <input
                          type="password"
                          id="confirmPassword"
                          placeholder="비밀번호를 다시 입력해주세요"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          required
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {step === 2 && (
              <div className={styles.stepContent}>
                <div className={styles.stepHeader}>
                  <h2>개인 정보를 입력해주세요</h2>
                  <p>서비스 이용을 위한 필수 정보입니다</p>
                </div>

                <div className={styles.inputGroup}>
                  <label htmlFor="name">이름</label>
                  <div className={styles.inputWrapper}>
                    <img src="/images/icons/user.svg" alt="이름" />
                    <input
                      type="text"
                      id="name"
                      placeholder="이름을 입력해주세요"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className={styles.inputGroup}>
                  <label htmlFor="phone">전화번호</label>
                  <div className={styles.inputWithButton}>
                    <div className={styles.inputWrapper}>
                      <img src="/images/icons/phone.svg" alt="전화번호" />
                      <input
                        type="tel"
                        id="phone"
                        placeholder="010-0000-0000"
                        value={phone}
                        onChange={(e) => {
                          setPhone(e.target.value);
                          setPhoneChecked(false);
                        }}
                        required
                        disabled={phoneVerified}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleCheckPhone}
                      disabled={
                        loading ||
                        phoneVerified ||
                        !phone ||
                        !validatePhone(phone)
                      }
                      className={`${styles.checkButton} ${
                        phoneVerified ? styles.checked : ""
                      }`}
                    >
                      {loading
                        ? "확인중..."
                        : phoneVerified
                        ? "확인완료"
                        : "중복확인"}
                    </button>
                  </div>
                  {phoneError && (
                    <div className={styles.fieldError}>{phoneError}</div>
                  )}
                </div>
              </div>
            )}

            {step === 3 && (
              <div className={styles.stepContent}>
                <div className={styles.stepHeader}>
                  <h2>추가 정보 및 약관 동의</h2>
                  <p>서비스 이용을 위한 마지막 단계입니다</p>
                </div>

                <div className={styles.inputGroup}>
                  <label>성별</label>
                  <div className={styles.radioGroup}>
                    <label className={styles.radioLabel}>
                      <input
                        type="radio"
                        name="gender"
                        value="male"
                        checked={gender === "male"}
                        onChange={() => setGender("male")}
                      />
                      <span>남성</span>
                    </label>
                    <label className={styles.radioLabel}>
                      <input
                        type="radio"
                        name="gender"
                        value="female"
                        checked={gender === "female"}
                        onChange={() => setGender("female")}
                      />
                      <span>여성</span>
                    </label>
                  </div>
                </div>

                <div className={styles.inputGroup}>
                  <label htmlFor="birthdate">생년월일</label>
                  <div className={styles.inputWrapper}>
                    <img src="/images/icons/calendar.svg" alt="생년월일" />
                    <input
                      type="date"
                      id="birthdate"
                      value={birthdate}
                      onChange={(e) => setBirthdate(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className={styles.termsSection}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={acceptTerms}
                      onChange={(e) => setAcceptTerms(e.target.checked)}
                    />
                    <span className={styles.checkmark}></span>
                    <span>[필수] 서비스 이용약관에 동의합니다</span>
                  </label>

                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={acceptNotifications}
                      onChange={(e) => setAcceptNotifications(e.target.checked)}
                    />
                    <span className={styles.checkmark}></span>
                    <span>[선택] 마케팅 정보 수신에 동의합니다</span>
                  </label>
                </div>
              </div>
            )}

            {/* 버튼 섹션 */}
            <div className={styles.buttonSection}>
              {step === 3 ? (
                <button
                  type="submit"
                  className={styles.submitButton}
                  disabled={loading}
                >
                  {loading ? (
                    <div className={styles.loadingSpinner}></div>
                  ) : (
                    "회원가입 완료"
                  )}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={goToNextStep}
                  className={styles.nextButton}
                >
                  다음 단계
                </button>
              )}
            </div>
          </form>
        </div>

        {/* 하단 링크 */}
        <div className={styles.bottomSection}>
          <div className={styles.linkContainer}>
            <span>이미 계정이 있으신가요?</span>
            <Link to="/login" className={styles.loginLink}>
              로그인
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
